import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getAllModulesWithProgress } from '../../selectors/progress';
import { getDischargeProgress } from '../../selectors/dischargeProgress';
import NavigationDropdown from './NavigationDropdown';

const getCurrentModule = (modules, pathArray) => {
  let currentModulePath;
  let currentModule;

  if (pathArray.includes('lessons')) {
    currentModulePath = pathArray[pathArray.indexOf('lessons') - 1];
  } else if (pathArray.includes('pages')) {
    currentModulePath = pathArray[pathArray.indexOf('pages') - 1];
  } else {
    currentModulePath = pathArray[pathArray.length - 1];
  }

  if (modules) {
    currentModule = modules[parseInt(currentModulePath, 10) - 1];
  }

  return currentModule;
};

/**
 * Gets the Lesson Id's of the current module
 *
 * @param { Array } modules the Array of the all the modules for the current id
 * @param { Array } pathArray an array of strings from the current path
 *
 * @returns { Array } currentModuleLessons An array of ids for the lessons related to the current
 *                     module based on current path
 */
const getCurrentModuleLessons = currentModule => {
  let currentModuleLessons = [];

  if (typeof currentModule !== 'undefined') {
    currentModuleLessons = currentModule.lessons || currentModule.topics;
  }

  return currentModuleLessons;
};

const getCurrentLesson = (lessons, pathArray) => {
  let currentLessonPath;
  let currentLesson = null;

  if (pathArray.includes('lessons')) {
    currentLessonPath = pathArray[pathArray.length - 3];
  }

  if (currentLessonPath) {
    currentLesson = lessons[parseInt(currentLessonPath, 10) - 1];
  }

  return currentLesson;
};

const getCurrentLessonTopics = currentLesson => {
  let currentLessonTopics = [];

  if (currentLesson) {
    currentLessonTopics = currentLesson.topics;
  }

  return currentLessonTopics;
};

const LearnNavigation = props => {
  const {
    location: { pathname },
    isDischarge,
    modules,
    dischargeSteps
  } = props;

  /**
   * The header is outside of react-router, so it has no access to match.params
   * which means it can't make use of the selectors in progress which require
   * router props to select lessons/steps/topics. Until we update react-router-dom and
   * start using the hooks API in the new router, we have to use these methods using
   * pathname to determine current lessons, lesson, topics, topic etc.
   *  */

  const pathnameArray = pathname.split('/');
  const isChapterPage =
    pathnameArray.includes('learn') && pathnameArray.length > 2;
  const isLessonPage = pathnameArray.includes('lesson');

  const [chaptersOpen, toggleChapters] = useState(false);
  const [lessonsOpen, toggleLessons] = useState(false);
  const [topicsOpen, toggleTopics] = useState(false);

  const currentModule = modules && getCurrentModule(modules, pathnameArray);
  const currentLessons =
    currentModule && getCurrentModuleLessons(currentModule);
  const currentLesson =
    currentLessons && getCurrentLesson(currentLessons, pathnameArray);
  const currentTopics = currentLesson && getCurrentLessonTopics(currentLesson);

  const currentDischargeStep =
    dischargeSteps && getCurrentModule(dischargeSteps, pathnameArray);
  const currentDischargeLessons =
    currentDischargeStep && getCurrentModuleLessons(currentDischargeStep);

  const node = useRef();

  const handleClick = e => {
    if (node.current.contains(e.target)) {
      return;
    }

    toggleChapters(false);
    toggleLessons(false);
    toggleTopics(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  // update the body class depending on whether one of the navigation dropdown menus are open
  // TODO: is there a better implementation using hooks or move to redux?
  // @see https://www.npmjs.com/package/body-scroll-lock
  // Refactor also see /src/actions/ui.js menuOpen()
  const setBodyNavigationDropdownOpen = isOpen => {
    const bodyClass = 'navigation-dropdown--open';
    if (isOpen) {
      document.body.classList.add(bodyClass);
    } else {
      document.body.classList.remove(bodyClass);
    }
  };

  return (
    <div className="header__secondary-nav-wrapper" ref={node}>
      <div className="header__secondary-item header__secondary-item--link">
        <NavigationDropdown
          itemType={!isDischarge ? 'module' : 'discharge'}
          items={!isDischarge ? modules : dischargeSteps}
          navigationTitle={!isDischarge ? 'Chapters' : 'Steps'}
          expanded={chaptersOpen}
          expandHandler={() => {
            toggleLessons(false);
            toggleTopics(false);
            toggleChapters(!chaptersOpen);
            setBodyNavigationDropdownOpen(!chaptersOpen);
          }}
        />
      </div>

      {isChapterPage && (
        <div className="header__secondary-item header__secondary-item--link">
          <NavigationDropdown
            parentSlug={pathnameArray[pathnameArray.length - 1]}
            itemType="lesson"
            currentModule={currentModule}
            items={!isDischarge ? currentLessons : currentDischargeLessons}
            navigationTitle="Lessons"
            expanded={lessonsOpen}
            expandHandler={() => {
              toggleChapters(false);
              toggleTopics(false);
              toggleLessons(!lessonsOpen);
              setBodyNavigationDropdownOpen(!lessonsOpen);
            }}
          />
        </div>
      )}

      {isLessonPage && (
        <div className="header__secondary-item header__secondary-item--link">
          <NavigationDropdown
            parentSlug={pathnameArray[pathnameArray.length - 1]}
            itemType="topic"
            items={currentTopics}
            navigationTitle="Topics"
            expanded={topicsOpen}
            expandHandler={() => {
              toggleChapters(false);
              toggleLessons(false);
              toggleTopics(!topicsOpen);
              setBodyNavigationDropdownOpen(!topicsOpen);
            }}
          />
        </div>
      )}

      {isDischarge &&
        currentDischargeLessons &&
        currentDischargeLessons.length > 0 && (
          <div className="header__secondary-item header__secondary-item--link">
            <NavigationDropdown
              parentSlug={pathnameArray[pathnameArray.length - 1]}
              itemType="discharge-topics"
              currentDischargeStep={currentDischargeStep}
              items={currentDischargeLessons}
              navigationTitle="Topics"
              expanded={topicsOpen}
              expandHandler={() => {
                toggleChapters(false);
                toggleLessons(false);
                toggleTopics(!topicsOpen);
                setBodyNavigationDropdownOpen(!topicsOpen);
              }}
            />
          </div>
        )}
    </div>
  );
};

LearnNavigation.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string
  }).isRequired,
  modules: PropTypes.arrayOf(PropTypes.shape({})),
  dischargeSteps: PropTypes.arrayOf(PropTypes.shape({})),
  isDischarge: PropTypes.bool.isRequired
};

LearnNavigation.defaultProps = {
  modules: undefined,
  dischargeSteps: undefined
};

const mapStateToProps = (state, ownProps) => ({
  location: ownProps.location,
  isDischarge: ownProps.isDischarge,
  modules: getAllModulesWithProgress(state),
  dischargeSteps: getDischargeProgress(state)
});

export default connect(mapStateToProps)(React.memo(LearnNavigation));



// WEBPACK FOOTER //
// ./src/components/NavigationDropdown/LearnNavigation.js